import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from 'app/core/triggerbee/triggerbee-api.enum';
import { Column } from '../../campaign/response-lists/response-lists.component';
import { DateRange } from '../date-range-chooser/date-range-chooser.model';
import { ResponseListsWithStatistics } from '../../campaign/campaign.model';

@Injectable({ providedIn: 'root' })
export class ResponseListsService {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('FormsService');
  }

  private getResourceBase() {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/response-lists`;
  }

  private getNpsResourceBase() {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/nps`;
  }

  private getCsatResourceBase() {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/csat`;
  }

  public getResponseLists(): Observable<any[]> {
    const url = `${this.getResourceBase()}`;
    return this.http.get(url);
  }

  public getResponseListsWithStatistics(): Observable<ResponseListsWithStatistics[]> {
    const url = `${this.getResourceBase()}/with-statistics`;
    return this.http.get(url);
  }

  public saveResponseListConfiguration(widgetId: number, responseListId: string): Observable<any> {
    const widgetsBaseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const url = `${widgetsBaseUrl}/widgets/${widgetId}/response-list`;
    const request = { responseListId: responseListId };
    return this.http.put(url, request);
  }

  public updateColumnList(responseListId: string, columns: string[]): Observable<any> {
    const url = `${this.getResourceBase()}/update-column-list/${responseListId}`;
    const request = { columns: columns };
    return this.http.put(url, request);
  }

  public getResponseList(responseListId: string): any {
    return this.http.get(`${this.getResourceBase()}/${responseListId}`);
  }

  public updateResponseList(responseListId: string, name: string): Observable<any> {
    const url = `${this.getResourceBase()}/${responseListId}`;
    const request = { name: name };
    return this.http.put(url, request);
  }

  public updateColumns(responseListId: string, columns: Column[], removeMissingColumns: boolean): Observable<any> {
    const url = `${this.getResourceBase()}/update-column-list/${responseListId}`;
    const request = {
      columns: columns,
      removeMissingColumns: removeMissingColumns,
    };
    return this.http.put(url, request);
  }

  public startExport(responseListIds: string[], exportType: string, onlyWithComments: boolean): Observable<any[]> {
    const url = `${this.getResourceBase()}/export-list`;
    const request = {
      responseListIds: responseListIds,
      exportType: exportType,
      onlyWithComments: onlyWithComments,
    };
    return this.http.post(url, request);
  }

  deleteResponseList(responseListId: string) {
    const url = `${this.getResourceBase()}/${responseListId}`;
    return this.http.delete(url);
  }

  createResponseList(name: any) {
    const url = `${this.getResourceBase()}`;
    const request = { name: name };
    return this.http.post(url, request);
  }

  public getWidgetByResponseListId(responseListId: string): any {
    const widgetsBase = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/widgets`;
    return this.http.get(`${widgetsBase}/?responseListId=${responseListId}`);
  }

  public getNpsStats(dateRange: DateRange): Observable<UxResponse[]> {
    const url = `${this.getNpsResourceBase()}/nps`;
    const request = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    return this.http.post(url, request);
  }

  public getNpsCommentsStats(dateRange: DateRange): Observable<CountResultResponse[]> {
    const url = `${this.getNpsResourceBase()}/comments-count`;
    const request = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    return this.http.post(url, request);
  }

  public getNpsResponsesOverTime(filterLists: string[]): Observable<any[]> {
    const params = filterLists.map((f) => `filterLists=${encodeURIComponent(f)}`).join('&');
    const url = `${this.getNpsResourceBase()}/responses-over-time?${params}`;
    return this.http.post(url, {});
  }

  public getCsatStats(dateRange: DateRange): Observable<UxResponse[]> {
    const url = `${this.getCsatResourceBase()}/stats`;
    const request = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    return this.http.post(url, request);
  }

  public getCsatCommentsStats(dateRange: DateRange): Observable<CountResultResponse[]> {
    const url = `${this.getCsatResourceBase()}/comments-count`;
    const request = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    return this.http.post(url, request);
  }

  public getCsatResponsesOverTime(filterLists: string[]): Observable<any[]> {
    const params = filterLists.map((f) => `filterLists=${encodeURIComponent(f)}`).join('&');
    const url = `${this.getCsatResourceBase()}/responses-over-time?${params}`;
    return this.http.post(url, {});
  }
}

export interface UxResponse {
  id: string;
  name: string;
  scoreColumn: string;
  commentColumn: string;
  data: UxDataResponse[];
}

export interface UxDataResponse {
  score: number;
  quantity: number;
}

export interface CountResultResponse {
  responseListId: string;
  count: number;
}
